<template>
    <v-container fluid fill-height class="console-metatags">
        <v-layout justify-center>
            <v-responsive max-width="1280px" width="100%">
                <v-row class="mt-8" justify="center" align="center">
                    <v-col cols="auto" class="pt-0 pb-0 headline text-start">메타태그</v-col>
                    <v-spacer></v-spacer>
                    <v-col cols="auto" class="pt-0 pb-0">
                        <metatag-variables-form>
                            <template #activator="{ attrs, on }">
                                <v-btn class="ma-2" color="primary" rounded outlined v-bind="{ ...attrs, loading }" v-on="on"><v-icon small class="mr-2">mdi-variable-box</v-icon>변수설정</v-btn>
                            </template>
                        </metatag-variables-form>
                        <v-btn class="ma-2" color="secondary" rounded @click="showSearch = !showSearch"><v-icon small class="mr-2">mdi-filter-variant</v-icon>검색필터</v-btn>
                    </v-col>
                </v-row>

                <list-search v-bind="{ showSearch }" @search="emitSearch">
                    <v-row>
                        <v-col cols="6" lg="3" class="py-0">
                            <v-text-field v-model="query.searchDateValue[0]" label="기간검색(시작일)" type="date" max="2999-12-31" clearable persistent-placeholder hide-details />
                        </v-col>
                        <v-col cols="6" lg="3" class="py-0">
                            <v-text-field v-model="query.searchDateValue[1]" label="기간검색(종료일)" type="date" max="2999-12-31" clearable persistent-placeholder hide-details />
                        </v-col>
                        <v-col cols="12" lg="3" class="py-0">
                            <v-select v-model="query.searchKey" :items="searchKeys" label="검색대상" placeholder="전체" clearable persistent-placeholder hide-details />
                        </v-col>
                        <v-col cols="12" lg="3" class="py-0">
                            <v-text-field v-model="query.searchValue" label="검색어" clearable persistent-placeholder hide-details @keydown.enter="emitSearch" />
                        </v-col>
                    </v-row>
                </list-search>

                <v-data-table :headers="headers" :items="items" :loading="loading" disable-sort disable-pagination hide-default-footer class="elevation-1 mt-4">
                    <template #[`item.code`]="{ item }">
                        <common-metatag-list-item-code v-bind="{ item }" />
                    </template>
                    <template #[`item.uses`]="{ item }">
                        <v-switch v-model="item.uses" inset hide-details color="primary" class="d-inline-block my-0 ml-0 mr-n2 pa-0" @change="(uses) => update({ _id: item._id, uses })" />
                    </template>
                    <template #[`item.clone`]="{ item }">
                        <metatag-form :value="item" is-clone @input="updateItem">
                            <template #activator="{ attrs, on }">
                                <v-btn icon tile small v-bind="attrs" v-on="on">
                                    <v-icon small>mdi-content-copy</v-icon>
                                </v-btn>
                            </template>
                        </metatag-form>
                    </template>
                    <template #[`item.update`]="{ item }">
                        <metatag-form :value="item" @input="updateItem">
                            <template #activator="{ attrs, on }">
                                <v-btn icon tile small v-bind="attrs" v-on="on">
                                    <v-icon small>mdi-pencil</v-icon>
                                </v-btn>
                            </template>
                        </metatag-form>
                    </template>
                    <template #[`item.delete`]="{ item }">
                        <v-btn icon tile small color="red" @click="remove(item)">
                            <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                    </template>
                </v-data-table>
                <v-pagination :value="page" :length="pageCount" :total-visible="11" class="mt-4 mb-12" @input="(page) => $router.push({ query: { ...$route.query, page } })"></v-pagination>
            </v-responsive>
        </v-layout>

        <metatag-form @input="updateItem">
            <template #activator="{ attrs, on }">
                <v-btn color="primary" fab fixed right bottom v-bind="attrs" v-on="on"><v-icon>mdi-plus</v-icon></v-btn>
            </template>
        </metatag-form>
    </v-container>
</template>

<script>
import api from "@/api";

import ListSearch from "@/components/console/dumb/list-search.vue";
import MetatagForm from "@/components/console/metatag/metatag-form.vue";
import MetatagVariablesForm from "@/components/console/metatag/metatag-variables-form.vue";
import CommonMetatagListItemCode from "@/components/console/metatag/list/common-metatag-list-item-code.vue";

const searchKeys = [{ text: "경로", value: "path" }];

// 검색 입력칸: 원본 list-search-default.vue 의 initQuery·sync·emit
let initQuery = (query = {}) => ({
    ...query,

    ["searchDateValue"]: [...(query["searchDateValue"] ?? [null, null])],

    ["searchKey"]: query?.["searchKey"] ?? null,
    ["searchValue"]: query?.["searchValue"] ?? null,
});

export default {
    components: {
        ListSearch,
        MetatagForm,
        MetatagVariablesForm,
        CommonMetatagListItemCode,
    },
    data: () => ({
        searchKeys,
        query: initQuery(),

        metatags: [],

        limit: 10,
        summary: { totalCount: 0 },

        loading: false,
        showSearch: true,

        headers: [
            { text: "경로", value: "path", width: 320 },
            { text: "코드", value: "code", cellClass: "pa-0" },
            { text: "사용여부", value: "uses", width: 80, align: "center" },
            { text: "복제", value: "clone", width: 56, align: "center", class: "px-0", cellClass: "px-0" },
            { text: "수정", value: "update", width: 56, align: "center", class: "px-0", cellClass: "px-0" },
            { text: "삭제", value: "delete", width: 56, align: "center", class: "px-0", cellClass: "px-0" },
        ],
    }),
    computed: {
        items() {
            return this.metatags.map((item) => ({ ...item }));
        },
        page() {
            return +(this.$route.query.page || "1");
        },
        skip() {
            return (this.page - 1) * this.limit;
        },
        pageCount() {
            return Math.ceil(this.summary.totalCount / this.limit) || 1;
        },
        params() {
            let { ...query } = this.$route.query;

            delete query.page;

            return { ...query };
        },
    },
    mounted() {
        this.syncQuery();
        this.init().then(this.search);
    },
    watch: {
        params() {
            this.init().then(this.search);
        },
        "$route.query"() {
            this.syncQuery();
        },
    },
    methods: {
        syncQuery() {
            this.query = initQuery(this.$route.query);
        },

        emitSearch() {
            const { ...query } = this.query || {};

            delete query.page;

            if (!query["searchDateValue"][0] && !query["searchDateValue"][1]) delete query["searchDateValue"];

            if (!query?.["searchKey"]) delete query["searchKey"];
            if (!query?.["searchValue"]) delete query["searchValue"];

            this.$router.push({ query }).catch(() => {});
        },

        async init() {
            this.metatags = [];
            this.summary = { totalCount: 0 };
        },

        async search() {
            if (this.loading) return;
            else this.loading = true;

            try {
                const { skip, limit, params } = this;
                const { summary, metatags } = await api.console.metatags.gets({
                    headers: { skip, limit },
                    params,
                });

                this.metatags = metatags;
                this.summary = summary;
            } finally {
                this.loading = false;
            }
        },

        async update(item) {
            if (this.loading) return;
            else this.loading = true;

            try {
                await api.console.metatags.put(item);
            } finally {
                this.loading = false;
                this.search();
            }
        },

        updateItem(item) {
            const index = this.metatags.findIndex(({ _id }) => _id == item._id);
            if (0 <= index) this.metatags.splice(index, 1, item);
            else {
                this.metatags = [item, ...this.metatags];
                this.summary.totalCount += 1;
            }
            this.search();
        },

        async remove(item) {
            if (!confirm("해당 항목을 삭제하시겠습니까?")) return;

            if (this.loading) return;
            else this.loading = true;

            try {
                await api.console.metatags.delete(item);
                this.loading = false;
                await this.search();
            } finally {
                if (this.loading) this.loading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.console-metatags ::v-deep td {
    vertical-align: top;
}
.console-metatags ::v-deep td:not(:last-of-type),
.console-metatags ::v-deep th:not(:last-of-type) {
    border-right: thin solid rgba(0, 0, 0, 0.12);
}
</style>
